import type { MouseEvent } from 'react';

/**
 * "Clicking the card opens it", for cards that CONTAIN other controls.
 *
 * Product asked for the whole card to be clickable on every tab, not just the
 * calendar (GRAVY-10342 retest). The calendar cards could simply become a
 * `<button>` — they hold nothing but text. The three queue cards cannot: they
 * carry a worker-profile link, a resolve pencil, a drill chevron. Nesting those
 * inside a button is invalid HTML, and giving the card `role="button"` is worse
 * — it claims a control that contains controls, which is precisely what
 * assistive tech cannot represent.
 *
 * So the card is NOT a control. It gains a mouse affordance, and the chevron
 * button stays exactly what it was: the single focusable, announced control for
 * the same action. Keyboard and screen-reader users are unaffected — they tab to
 * the chevron as before — while a mouse user can click anywhere.
 *
 * This is deliberately not `role="button" + tabIndex`, which would put a second
 * stop in the tab order announcing the same action, wrapping the very controls
 * it duplicates.
 */

/** Ignore a click that ended a text selection — the user was reading, not navigating. */
const isSelecting = (): boolean => {
  const selection =
    typeof window === 'undefined' ? null : window.getSelection();
  return (
    !!selection && selection.type === 'Range' && selection.toString().length > 0
  );
};

/**
 * Props for the card ROOT. `undefined` when there is nothing to open, so the
 * card stays inert rather than pretending to be clickable.
 */
export const cardActivation = (onOpen?: () => void) => {
  if (!onOpen) return {};

  return {
    onClick: (e: MouseEvent<HTMLElement>) => {
      // An embedded control already handled it (see `stopCardActivation`), or
      // the click landed on something interactive we do not own — a link, a
      // button, an input. Walking the DOM is what keeps this correct when a new
      // control is added to a card without anyone remembering this file.
      const target = e.target as HTMLElement | null;
      if (
        target?.closest('a, button, input, select, textarea, [role="button"]')
      ) {
        return;
      }
      if (isSelecting()) return;
      onOpen();
    },
    'data-card-activation': 'true' as const,
  };
};

/**
 * For an embedded control that must NOT also open the card.
 *
 * The DOM check in `cardActivation` covers buttons and links already; this is
 * for the cases it cannot see — a control that stops being a `<button>`, or one
 * whose handler opens something of its own (a modal) where bubbling would open
 * the panel behind it.
 */
export const stopCardActivation = (e: MouseEvent<HTMLElement>): void => {
  e.stopPropagation();
};

export default cardActivation;
